import { TextField, Grid, Typography, Paper, FormControl, InputLabel, Select, MenuItem } from "@mui/material";
import { TimePicker } from "@mui/x-date-pickers";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { useForm, Controller } from "react-hook-form";
import {
    createFormattedTimeOffs,
    findTimeOffDaysWithInvalidTimes,
    timeOffTypes,
    DayTimeOffInput,
} from "../utils/scheduleUtils";
import { useCreateRecord } from "../../../api/useCreateRecord";
import { useStore } from "../../../store/store";

import { SchedulesHeader } from "./SchedulesHeader";
import { secondaryColor } from "../../../theme";
import { weekDays, weekDaysTranslation } from "../../../utils/constants";

type TimeOffFormData = {
    user: string;
    startDate: Date | null;
    endDate: Date | null;
    time_off: Record<string, DayTimeOffInput>;
};



export const TimeOffAddMultiple = () => {
    const createTimeOffs = useCreateRecord();
    const {
        notifications: { setNotification },
    } = useStore();

    const entityForm = useForm<TimeOffFormData>({
        defaultValues: {
            user: "",
            startDate: null,
            endDate: null,
            time_off: weekDays.reduce(
                (acc, day) => ({ ...acc, [day]: { type: "", notes: "", start: null, end: null } }),
                {} as Record<string, DayTimeOffInput>
            ),
        },
    });

    const onSubmit = async (data: any) => {
        if (!data.startDate || !data.endDate || !data.user) {
            setNotification({ isOpen: true, message: "Please fill in all required fields", severity: "error" });
            return;
        }

        const daysWithInvalidTimes = findTimeOffDaysWithInvalidTimes(data.time_off);
        if (daysWithInvalidTimes.length > 0) {
            const dayNames = daysWithInvalidTimes.map((day) => weekDaysTranslation[day]).join(", ");
            setNotification({
                isOpen: true,
                message: `Preencha horas de início e fim válidas em: ${dayNames}. As horas registadas são exatamente as que contam.`,
                severity: "error",
            });
            return;
        }

        const formattedTimeOffs = createFormattedTimeOffs(
            data.startDate,
            data.endDate,
            data.user,
            data.clinic,
            data.time_off
        );


        if (formattedTimeOffs.length === 0) {
            setNotification({ isOpen: true, message: "Please select at least one time off type", severity: "error" });
            return;
        }

        try {
            await createTimeOffs.mutateAsync({ entity: "time_off", data: formattedTimeOffs });
            setNotification({ isOpen: true, message: "Time offs created successfully", severity: "success" });
        } catch (error: any) {
            const errorMessage = error.response.data.errors.map((error: any) => error.message).join(", ");
            setNotification({ isOpen: true, message: errorMessage, severity: "error" });
        }
    };

    const hasTimeOffSelected = (day: string) => {
        const timeOffData = entityForm.watch("time_off");
        return timeOffData[day]?.type && timeOffData[day].type !== "";
    };

    return (
        <LocalizationProvider dateAdapter={AdapterDateFns}>
            <form onSubmit={entityForm.handleSubmit(onSubmit)}>
                <SchedulesHeader entityForm={entityForm} hideRooms={true} />
                <Grid container spacing={2} p={2}>
                    {weekDays.map((day, index) => (
                        <Paper
                            elevation={0}
                            sx={{
                                backgroundColor: "background.paper",
                                p: 3,
                                width: "100%",
                                mt: 6,
                                borderRadius: 4,
                                boxShadow: hasTimeOffSelected(day) ? `0 0 20px ${secondaryColor}30` : "none",
                                border: hasTimeOffSelected(day)
                                    ? `2px solid ${secondaryColor}30`
                                    : "1px solid rgba(0, 0, 0, 0.12)",
                                transition: "all 0.3s ease-in-out",
                            }}
                            key={day}
                        >
                            <Grid item xs={12} mb={3}>
                                <Typography variant="h6" color={hasTimeOffSelected(day) ? "primary" : "text.secondary"} mb={2}>
                                    {weekDaysTranslation[day]}
                                </Typography>
                            </Grid>
                            
                            <Grid container spacing={1}>
                                <Grid item xs={3}>
                                    <FormControl fullWidth>
                                        <InputLabel id={`time-off-type-${day}`}>Tipo de Registo</InputLabel>
                                        <Controller
                                            name={`time_off.${day}.type`}
                                            control={entityForm.control}
                                            defaultValue=""
                                            render={({ field }) => (
                                                <Select
                                                    labelId={`time-off-type-${day}`}
                                                    label="Tipo de Registo"
                                                    value={field.value}
                                                    onChange={(e) => field.onChange(e.target.value)}
                                                    fullWidth
                                                >
                                                    <MenuItem value="">
                                                        <em>Nenhum</em>
                                                    </MenuItem>
                                                    {timeOffTypes.map((type) => (
                                                        <MenuItem key={type.value} value={type.value}>
                                                            {type.label}
                                                        </MenuItem>
                                                    ))}
                                                </Select>
                                            )}
                                        />
                                    </FormControl>
                                </Grid>

                                <Grid item xs={2.5}>
                                    <Controller
                                        name={`time_off.${day}.start`}
                                        control={entityForm.control}
                                        defaultValue={null}
                                        render={({ field }) => (
                                            <TimePicker {...field} sx={{ width: "100%" }} label="Hora de início" ampm={false} />
                                        )}
                                    />
                                </Grid>

                                <Grid item xs={2.5}>
                                    <Controller
                                        name={`time_off.${day}.end`}
                                        control={entityForm.control}
                                        defaultValue={null}
                                        render={({ field }) => (
                                            <TimePicker {...field} sx={{ width: "100%" }} label="Hora de fim" ampm={false} />
                                        )}
                                    />
                                </Grid>

                                <Grid item xs={4}>
                                    <Controller
                                        name={`time_off.${day}.notes`}
                                        control={entityForm.control}
                                        defaultValue=""
                                        render={({ field }) => (
                                            <TextField
                                                {...field}
                                                label="Notas"
                                                fullWidth
                                                multiline
                                                rows={1}
                                            />
                                        )}
                                    />
                                </Grid>
                            </Grid>
                        </Paper>
                    ))}
                </Grid>
            </form>
        </LocalizationProvider>
    );
};
